import { usePageTitle } from '../hooks/usePageTitle.js';
import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import { api, type KbArticle, type KbRevision, type SessionRow, type ScopeJobRow, type ConflictRow, type MfaConfig, type MfaMethod, type LoginConfig } from '../lib/api.js';
import { ScopeJobForm } from '../components/ScopeJobForm.js';
import { RecipeReviewModal } from '../components/RecipeReviewModal.js';
import { JiraPanel } from '../components/JiraPanel.js';
import { FlatFileRecipesTab } from '../components/FlatFileRecipesTab.js';

// ── Helpers ────────────────────────────────────────────────────────────────────

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function scopeStatusColor(status: string): string {
  switch (status) {
    case 'accepted': return 'var(--accent-green)';
    case 'awaiting_review': return 'var(--accent-yellow)';
    case 'running': case 'queued': return 'var(--accent-blue)';
    case 'failed': return 'var(--accent-red)';
    default: return 'var(--text-dimmed)';
  }
}

function sessionStatusColor(status: string): string {
  switch (status) {
    case 'completed': return 'var(--accent-green)';
    case 'running': return 'var(--accent-blue)';
    case 'failed': return 'var(--accent-red)';
    case 'draft': return 'var(--text-dimmed)';
    default: return 'var(--text-dimmed)';
  }
}

// ── Stat mini card ─────────────────────────────────────────────────────────────

function MiniStat({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <div
      className="rounded-xl p-3 flex flex-col gap-1"
      style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)' }}
    >
      <div className="text-xl font-bold" style={{ color: color ?? 'var(--text-primary)' }}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: 'var(--text-dimmed)' }}>{label}</div>
    </div>
  );
}

// ── MFA Config Card ────────────────────────────────────────────────────────────

const MFA_METHOD_LABELS: Record<MfaMethod, string> = {
  totp: 'TOTP (Authenticator App)',
  fastmail: 'Fastmail Email Code',
  sms: 'SMS via Twilio',
};

function MfaConfigCard({ ehrId, current }: { ehrId: string; current: MfaConfig | null }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState<MfaMethod>(current?.method ?? 'totp');
  const [totpSecret, setTotpSecret] = useState('');
  // Modern Fastmail JMAP fields
  const [fmToken, setFmToken] = useState('');
  const [fmFromAddress, setFmFromAddress] = useState((current as { fastmail?: { fromAddress?: string } })?.fastmail?.fromAddress ?? '');
  const [fmSubject, setFmSubject] = useState((current as { fastmail?: { subjectFilter?: string } })?.fastmail?.subjectFilter ?? '');
  const [fmFolder, setFmFolder] = useState((current as { fastmail?: { folder?: string } })?.fastmail?.folder ?? 'Imports');
  const [twilioSid, setTwilioSid] = useState(current?.twilioAccountSid ?? '');
  const [twilioToken, setTwilioToken] = useState('');
  const [twilioFrom, setTwilioFrom] = useState(current?.twilioFromNumber ?? '');
  const [codeInputSel, setCodeInputSel] = useState(current?.codeInputSelector ?? '');
  const [codeSubmitSel, setCodeSubmitSel] = useState(current?.codeSubmitSelector ?? '');

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { method };
      if (method === 'totp') { if (totpSecret) body.totpSecret = totpSecret; }
      if (method === 'fastmail') {
        // Always send the modern JMAP shape. Token must be provided to save
        // (or kept blank to retain the existing encrypted token — server
        // will preserve when missing).
        const fastmail: Record<string, string> = {
          fromAddress: fmFromAddress,
          subjectFilter: fmSubject,
          folder: fmFolder || 'Imports',
        };
        if (fmToken) fastmail.token = fmToken;
        // Only attach fastmail block when token is provided OR an existing one is present
        if (fmToken || (current as { fastmail?: { token?: string } })?.fastmail?.token) {
          // If no new token, server keeps existing — we still send the metadata
          // but include a placeholder key the server treats as "keep existing".
          if (!fmToken) {
            // Re-send the existing encrypted token so the API zod schema is satisfied
            const existing = (current as { fastmail?: { token?: string } })?.fastmail?.token;
            if (existing) fastmail.token = existing;
          }
          body.fastmail = fastmail;
        } else {
          throw new Error('Fastmail token is required');
        }
      }
      if (method === 'sms') {
        body.twilioAccountSid = twilioSid;
        if (twilioToken) body.twilioAuthToken = twilioToken;
        body.twilioFromNumber = twilioFrom;
      }
      if (codeInputSel) body.codeInputSelector = codeInputSel;
      if (codeSubmitSel) body.codeSubmitSelector = codeSubmitSel;
      return api.saveMfaConfig(ehrId, body as unknown as MfaConfig);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ehr', ehrId] });
      toast.success('MFA config saved');
      setOpen(false);
    },
    onError: (err) => toast.error(`Save failed: ${err instanceof Error ? err.message : String(err)}`),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteMfaConfig(ehrId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ehr', ehrId] });
      toast.success('MFA config removed');
    },
    onError: (err) => toast.error(`Remove failed: ${err instanceof Error ? err.message : String(err)}`),
  });

  const inputCls = 'w-full rounded-lg px-2.5 py-1.5 text-xs font-mono outline-none';
  const inputStyle = { background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-primary)' };

  return (
    <div className="glass rounded-2xl p-5" style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-text-secondary">2FA / MFA</div>
        <div className="flex items-center gap-2">
          {current && (
            <button
              type="button"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              className="text-[10px] font-semibold px-2 py-0.5 rounded"
              style={{ color: 'var(--accent-red)', background: 'rgba(238,93,80,0.1)', border: '1px solid rgba(238,93,80,0.2)' }}
            >
              Remove
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="text-[10px] font-semibold px-2 py-0.5 rounded"
            style={{ color: '#0dcfb8', background: 'rgba(13,207,184,0.1)', border: '1px solid rgba(13,207,184,0.2)' }}
          >
            {open ? 'Close' : current ? 'Edit' : 'Configure'}
          </button>
        </div>
      </div>

      {/* Status line */}
      {!open && (
        current ? (
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full" style={{ background: 'var(--accent-green)' }} />
            <span className="text-xs" style={{ color: 'var(--accent-green)' }}>{MFA_METHOD_LABELS[current.method]} configured</span>
          </div>
        ) : (
          <div className="text-xs text-text-dimmed">Not configured — click Configure to enable 2FA auto-fill during scoping.</div>
        )
      )}

      {/* Editor */}
      {open && (
        <div className="space-y-3 mt-2">
          {/* Method */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest mb-1 text-text-secondary">Method</div>
            <div className="grid grid-cols-3 gap-1.5">
              {(['totp', 'fastmail', 'sms'] as MfaMethod[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMethod(m)}
                  className="rounded-lg px-2 py-1.5 text-[10px] font-semibold text-center"
                  style={method === m
                    ? { background: 'linear-gradient(135deg, rgba(13,207,184,0.2),rgba(6,182,212,0.2))', border: '1px solid rgba(13,207,184,0.4)', color: 'var(--text-primary)' }
                    : { background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-dimmed)' }}
                >
                  {m === 'totp' ? 'TOTP' : m === 'fastmail' ? 'Fastmail' : 'SMS'}
                </button>
              ))}
            </div>
            {method === 'sms' && (
              <div className="mt-1.5 rounded-lg px-2.5 py-1.5 text-[10px]" style={{ background: 'rgba(255,209,102,0.08)', border: '1px solid rgba(255,209,102,0.2)', color: 'var(--accent-yellow)' }}>
                SMS (Twilio) is scaffolded but not yet implemented
              </div>
            )}
          </div>

          {method === 'totp' && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-widest mb-1 text-text-secondary">TOTP secret (base32)</div>
              <input type="password" value={totpSecret} onChange={(e) => setTotpSecret(e.target.value)} placeholder={current?.totpSecret ? '••• leave blank to keep existing •••' : 'JBSWY3DPEHPK3PXP…'} className={inputCls} style={inputStyle} />
            </div>
          )}

          {method === 'fastmail' && (
            <div className="space-y-2">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-widest mb-1 text-text-secondary">JMAP API token</div>
                <input
                  type="password"
                  value={fmToken}
                  onChange={(e) => setFmToken(e.target.value)}
                  placeholder={(current as { fastmail?: { token?: string } })?.fastmail?.token ? '••• leave blank to keep existing token •••' : 'fmu1-…'}
                  className={inputCls}
                  style={inputStyle}
                />
                <div className="text-[10px] text-text-dimmed mt-0.5">
                  fastmail.com → Settings → Privacy & Security → API Tokens (scope: Read mail)
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest mb-1 text-text-secondary">From address</div>
                  <input
                    value={fmFromAddress}
                    onChange={(e) => setFmFromAddress(e.target.value)}
                    placeholder="support@ehr.com"
                    className={inputCls}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest mb-1 text-text-secondary">Folder</div>
                  <input
                    value={fmFolder}
                    onChange={(e) => setFmFolder(e.target.value)}
                    placeholder="Imports"
                    className={inputCls}
                    style={inputStyle}
                  />
                </div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-widest mb-1 text-text-secondary">Subject filter</div>
                <input
                  value={fmSubject}
                  onChange={(e) => setFmSubject(e.target.value)}
                  placeholder="Your verification code"
                  className={inputCls}
                  style={inputStyle}
                />
              </div>
            </div>
          )}

          {method === 'sms' && (
            <div className="space-y-2">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-widest mb-1 text-text-secondary">Twilio account SID</div>
                <input value={twilioSid} onChange={(e) => setTwilioSid(e.target.value)} placeholder="ACxxxxxxxxxxxxxxxx" className={inputCls} style={inputStyle} />
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-widest mb-1 text-text-secondary">Auth token</div>
                <input type="password" value={twilioToken} onChange={(e) => setTwilioToken(e.target.value)} placeholder={current?.twilioAuthToken ? '••• keep existing •••' : ''} className={inputCls} style={inputStyle} />
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-widest mb-1 text-text-secondary">From number</div>
                <input value={twilioFrom} onChange={(e) => setTwilioFrom(e.target.value)} placeholder="+15550001234" className={inputCls} style={inputStyle} />
              </div>
            </div>
          )}

          {/* Optional overrides */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-widest mb-1 text-text-secondary">Code input selector <span className="font-normal opacity-60">(optional)</span></div>
              <input value={codeInputSel} onChange={(e) => setCodeInputSel(e.target.value)} placeholder="auto-detect" className={inputCls} style={inputStyle} />
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-widest mb-1 text-text-secondary">Submit selector <span className="font-normal opacity-60">(optional)</span></div>
              <input value={codeSubmitSel} onChange={(e) => setCodeSubmitSel(e.target.value)} placeholder="auto-detect" className={inputCls} style={inputStyle} />
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="rounded-xl px-4 py-1.5 text-xs font-semibold text-white transition-opacity disabled:opacity-40"
              style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
            >
              {save.isPending ? 'Saving…' : 'Save MFA config'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Login Config Card ─────────────────────────────────────────────────────────

function LoginConfigCard({ ehrId, current }: { ehrId: string; current: LoginConfig | null }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [loginPath, setLoginPath] = useState(current?.loginPath ?? '');
  const [usernameSel, setUsernameSel] = useState(current?.selectors.username ?? '');
  const [passwordSel, setPasswordSel] = useState(current?.selectors.password ?? '');
  const [submitSel, setSubmitSel] = useState(current?.selectors.submit ?? '');
  const [waitForUrl, setWaitForUrl] = useState(current?.waitForUrl ?? '');
  const [otpContainer, setOtpContainer] = useState(current?.otp?.container ?? '');
  const [otpSubmit, setOtpSubmit] = useState(current?.otp?.submitButton ?? '');
  const [tokenMethod, setTokenMethod] = useState<'none' | 'network' | 'cookie' | 'localStorage'>(
    current?.extractToken?.method ?? 'none'
  );
  const [tokenUrlPattern, setTokenUrlPattern] = useState(
    current?.extractToken && 'urlPattern' in current.extractToken ? current.extractToken.urlPattern : ''
  );

  const save = useMutation({
    mutationFn: () => {
      const body: LoginConfig = {
        loginPath: loginPath.trim() || '/',
        selectors: { username: usernameSel.trim(), password: passwordSel.trim(), submit: submitSel.trim() },
      };
      if (waitForUrl.trim()) body.waitForUrl = waitForUrl.trim();
      if (otpContainer.trim() && otpSubmit.trim()) {
        body.otp = { container: otpContainer.trim(), submitButton: otpSubmit.trim() };
      }
      if (tokenMethod === 'network' && tokenUrlPattern.trim()) {
        body.extractToken = { method: 'network', urlPattern: tokenUrlPattern.trim() };
      } else if (tokenMethod !== 'none') {
        body.extractToken = { method: 'none' };
      }
      return api.saveLoginConfig(ehrId, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ehr', ehrId] });
      toast.success('Login config saved');
      setOpen(false);
    },
    onError: (err) => toast.error(`Save failed: ${err instanceof Error ? err.message : String(err)}`),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteLoginConfig(ehrId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ehr', ehrId] });
      toast.success('Login config removed');
    },
    onError: (err) => toast.error(`Remove failed: ${err instanceof Error ? err.message : String(err)}`),
  });

  const inputCls = 'w-full rounded-lg px-2.5 py-1.5 text-xs font-mono outline-none';
  const inputStyle = { background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-primary)' };

  return (
    <div className="glass rounded-2xl p-5" style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-text-secondary">Login Config (deterministic)</div>
        <div className="flex items-center gap-2">
          {current && (
            <button
              type="button"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              className="text-[10px] font-semibold px-2 py-0.5 rounded"
              style={{ color: 'var(--accent-red)', background: 'rgba(238,93,80,0.1)', border: '1px solid rgba(238,93,80,0.2)' }}
            >
              Remove
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="text-[10px] font-semibold px-2 py-0.5 rounded"
            style={{ color: '#0dcfb8', background: 'rgba(13,207,184,0.1)', border: '1px solid rgba(13,207,184,0.2)' }}
          >
            {open ? 'Close' : current ? 'Edit' : 'Configure'}
          </button>
        </div>
      </div>

      {!open && (
        current ? (
          <div className="space-y-1 text-xs">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full" style={{ background: 'var(--accent-green)' }} />
              <span style={{ color: 'var(--accent-green)' }}>Configured — scoping will skip AI selector inference</span>
            </div>
            <div className="text-text-dimmed font-mono">
              loginPath: {current.loginPath}
              {current.otp && ` · otp: ${current.otp.container}`}
              {current.waitForUrl && ` · wait: ${current.waitForUrl}`}
            </div>
          </div>
        ) : (
          <div className="text-xs text-text-dimmed">
            Not set — scoping will use AI to infer selectors. Configure deterministic selectors here for reliable repeated scopes.
          </div>
        )
      )}

      {open && (
        <div className="space-y-3 mt-2">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest mb-1 text-text-secondary">Login path</div>
            <input value={loginPath} onChange={(e) => setLoginPath(e.target.value)} placeholder="/login" className={inputCls} style={inputStyle} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-widest mb-1 text-text-secondary">Username selector</div>
              <input value={usernameSel} onChange={(e) => setUsernameSel(e.target.value)} placeholder="#username" className={inputCls} style={inputStyle} />
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-widest mb-1 text-text-secondary">Password selector</div>
              <input value={passwordSel} onChange={(e) => setPasswordSel(e.target.value)} placeholder="#password" className={inputCls} style={inputStyle} />
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-widest mb-1 text-text-secondary">Submit selector</div>
              <input value={submitSel} onChange={(e) => setSubmitSel(e.target.value)} placeholder="#submit" className={inputCls} style={inputStyle} />
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest mb-1 text-text-secondary">Wait-for URL substring (after login)</div>
            <input value={waitForUrl} onChange={(e) => setWaitForUrl(e.target.value)} placeholder="/dashboard" className={inputCls} style={inputStyle} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-widest mb-1 text-text-secondary">OTP container selector <span className="font-normal opacity-60">(optional)</span></div>
              <input value={otpContainer} onChange={(e) => setOtpContainer(e.target.value)} placeholder="#verify-otp-container" className={inputCls} style={inputStyle} />
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-widest mb-1 text-text-secondary">OTP submit button</div>
              <input value={otpSubmit} onChange={(e) => setOtpSubmit(e.target.value)} placeholder="#verify-code-btn" className={inputCls} style={inputStyle} />
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest mb-1 text-text-secondary">Token extraction method</div>
            <div className="grid grid-cols-2 gap-1.5">
              {(['none', 'network'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setTokenMethod(m)}
                  className="rounded-lg px-2 py-1.5 text-[10px] font-semibold text-center"
                  style={tokenMethod === m
                    ? { background: 'linear-gradient(135deg, rgba(13,207,184,0.2),rgba(6,182,212,0.2))', border: '1px solid rgba(13,207,184,0.4)', color: 'var(--text-primary)' }
                    : { background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-dimmed)' }}
                >
                  {m === 'none' ? 'None' : 'From XHR'}
                </button>
              ))}
            </div>
            {tokenMethod === 'network' && (
              <input
                value={tokenUrlPattern}
                onChange={(e) => setTokenUrlPattern(e.target.value)}
                placeholder="URL pattern, e.g. /api/"
                className={`${inputCls} mt-2`}
                style={inputStyle}
              />
            )}
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={save.isPending || !usernameSel || !passwordSel || !submitSel || !loginPath}
              className="rounded-xl px-4 py-1.5 text-xs font-semibold text-white transition-opacity disabled:opacity-40"
              style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
            >
              {save.isPending ? 'Saving…' : 'Save Login Config'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Overview tab ──────────────────────────────────────────────────────────────

function OverviewTab({
  ehrId,
  ehr,
  scopeJobs,
  sessions,
  conflicts,
  sessionStats,
  onReviewScope,
  onStartScope,
}: {
  ehrId: string;
  ehr: { displayName: string; slug: string; authStyle: string; loginUrl: string | null; baseUrlTemplate: string; recipePath: string | null; mfaConfig: MfaConfig | null; loginConfig: LoginConfig | null; stats: { sessions: number; lastRunAt: string | null; totalRecords: number; openConflicts: number } };
  scopeJobs: ScopeJobRow[];
  sessions: SessionRow[];
  conflicts: ConflictRow[];
  sessionStats: { total: number; completed: number; running: number; failed: number; draft: number };
  onReviewScope: (id: string) => void;
  onStartScope: () => void;
}) {
  const { data: jiraData } = useQuery({
    queryKey: ['jira', ehr.displayName],
    queryFn: () => api.listJiraIssues(undefined, ehr.displayName),
    staleTime: 60_000,
  });

  const { data: kbData } = useQuery({
    queryKey: ['kb', ehrId],
    queryFn: () => api.listKbArticles(ehrId),
  });

  const jiraIssues = jiraData?.issues ?? [];
  const kbArticles = kbData?.articles ?? [];
  const latestScope = scopeJobs[0] ?? null;
  const openTickets = jiraIssues.filter((t) => t.statusCategory !== 'done');
  const doneTickets = jiraIssues.filter((t) => t.statusCategory === 'done');
  const goLiveDates = jiraIssues.filter((t) => t.goLiveDate).map((t) => t.goLiveDate!).sort();
  const nextGoLive = goLiveDates[0] ?? null;

  return (
    <div className="p-6 space-y-6 max-w-[1200px]">

      {/* ── Hero stats row ─────────────────────────────────────────── */}
      <div className="grid grid-cols-6 gap-3">
        <MiniStat label="Total sessions" value={sessionStats.total} />
        <MiniStat label="Completed" value={sessionStats.completed} color="var(--accent-green)" />
        <MiniStat label="Running" value={sessionStats.running} color="var(--accent-blue)" />
        <MiniStat label="Failed" value={sessionStats.failed} color={sessionStats.failed > 0 ? 'var(--accent-red)' : undefined} />
        <MiniStat label="Open conflicts" value={conflicts.length} color={conflicts.length > 0 ? 'var(--accent-yellow)' : undefined} />
        <MiniStat label="Jira tickets" value={jiraIssues.length} />
      </div>

      {/* ── Main grid ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-12 gap-4">

        {/* Recipe / scope status — col 4 */}
        <div className="col-span-4 space-y-4">

          {/* Recipe status card */}
          <div className="glass rounded-2xl p-5" style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-text-secondary mb-3">Recipe</div>
            {ehr.recipePath ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ background: 'var(--accent-green)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--accent-green)' }}>Active recipe</span>
                </div>
                <div className="font-mono text-[11px] text-text-dimmed break-all">{ehr.recipePath}</div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ background: 'var(--text-dimmed)' }} />
                  <span className="text-sm text-text-dimmed">No recipe yet</span>
                </div>
                <button
                  type="button"
                  onClick={onStartScope}
                  className="w-full rounded-xl py-2 text-xs font-semibold text-white"
                  style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
                >
                  + Run scope job
                </button>
              </div>
            )}
            <div className="border-t border-border mt-4 pt-3 space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-text-dimmed">Auth style</span>
                <span className="text-text-secondary font-mono">{ehr.authStyle}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-dimmed">Base URL</span>
                <span className="text-text-secondary font-mono truncate max-w-[140px]">{ehr.baseUrlTemplate || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-dimmed">Login URL</span>
                <span className="text-text-secondary font-mono truncate max-w-[140px]">{ehr.loginUrl || '—'}</span>
              </div>
            </div>
          </div>

          {/* Go-live / calendar */}
          <div className="glass rounded-2xl p-5" style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-text-secondary mb-3">Calendar</div>
            {nextGoLive ? (
              <div className="space-y-1">
                <div className="text-xs text-text-dimmed">Next go-live</div>
                <div className="text-lg font-bold text-text-primary">{nextGoLive}</div>
              </div>
            ) : (
              <div className="text-sm text-text-dimmed">No go-live dates in Jira</div>
            )}
            {goLiveDates.length > 1 && (
              <div className="mt-3 space-y-1">
                {goLiveDates.slice(1, 4).map((d, i) => (
                  <div key={i} className="text-xs text-text-dimmed">{d}</div>
                ))}
              </div>
            )}
          </div>

          {/* KB snippet */}
          <div className="glass rounded-2xl p-5" style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-text-secondary">Knowledge Base</div>
              <span className="text-[10px] text-text-dimmed">{kbArticles.length} page{kbArticles.length !== 1 ? 's' : ''}</span>
            </div>
            {kbArticles.length === 0 ? (
              <div className="text-sm text-text-dimmed">No KB articles yet.</div>
            ) : (
              <div className="space-y-1">
                {kbArticles.slice(0, 5).map((a) => (
                  <div key={a.id} className="text-xs text-text-secondary truncate">· {a.title}</div>
                ))}
                {kbArticles.length > 5 && (
                  <div className="text-[10px] text-text-dimmed">+{kbArticles.length - 5} more</div>
                )}
              </div>
            )}
          </div>

          {/* Login config (deterministic) */}
          <LoginConfigCard ehrId={ehrId} current={ehr.loginConfig ?? null} />

          {/* MFA config */}
          <MfaConfigCard ehrId={ehrId} current={ehr.mfaConfig} />
        </div>

        {/* Scope run history — col 4 */}
        <div className="col-span-4">
          <div className="glass rounded-2xl p-5 h-full" style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-text-secondary">Scope Runs</div>
              <button
                type="button"
                onClick={onStartScope}
                className="text-[10px] font-semibold px-2 py-1 rounded-lg"
                style={{ background: 'rgba(13,207,184,0.12)', color: '#0dcfb8', border: '1px solid rgba(13,207,184,0.25)' }}
              >
                + New
              </button>
            </div>
            {scopeJobs.length === 0 ? (
              <div className="text-sm text-text-dimmed">No scope runs yet.</div>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-y-auto">
                {scopeJobs.map((j) => (
                  <div
                    key={j.id}
                    className="rounded-xl p-3"
                    style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)' }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: scopeStatusColor(j.status) }} />
                        <span className="text-xs font-medium capitalize text-text-primary">{j.status.replace(/_/g, ' ')}</span>
                      </div>
                      <span className="text-[10px] text-text-dimmed">{timeAgo(j.createdAt)}</span>
                    </div>
                    {j.notes && (
                      <div className="text-[11px] text-text-dimmed mt-1 truncate">{j.notes}</div>
                    )}
                    {j.status === 'awaiting_review' && (
                      <button
                        type="button"
                        onClick={() => onReviewScope(j.id)}
                        className="mt-2 text-[10px] font-semibold px-2 py-0.5 rounded"
                        style={{ background: 'rgba(255,209,102,0.15)', color: 'var(--accent-yellow)', border: '1px solid rgba(255,209,102,0.3)' }}
                      >
                        Review draft →
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent sessions — col 4 */}
        <div className="col-span-4">
          <div className="glass rounded-2xl p-5 h-full" style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-text-secondary mb-4">Recent Sessions</div>
            {sessions.length === 0 ? (
              <div className="text-sm text-text-dimmed">No sessions yet.</div>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-y-auto">
                {sessions.slice(0, 15).map((s) => (
                  <Link
                    key={s.id}
                    to="/sessions/$id"
                    params={{ id: s.id }}
                    className="block rounded-xl p-3 transition-colors"
                    style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)' }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: sessionStatusColor(s.status) }} />
                        <span className="text-xs font-medium text-text-primary truncate">{s.title ?? s.id.slice(0, 8)}</span>
                      </div>
                      <span className="text-[10px] text-text-dimmed shrink-0 ml-2">{timeAgo(s.createdAt)}</span>
                    </div>
                    <div className="text-[10px] capitalize mt-0.5 ml-3.5" style={{ color: sessionStatusColor(s.status) }}>{s.status}</div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Conflicts + Jira row ───────────────────────────────────── */}
      <div className="grid grid-cols-12 gap-4">

        {/* Open conflicts */}
        <div className="col-span-5">
          <div className="glass rounded-2xl p-5" style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-text-secondary">Open Conflicts</div>
              {conflicts.length > 0 && (
                <Link to="/conflicts" className="text-[10px]" style={{ color: '#0dcfb8' }}>View all →</Link>
              )}
            </div>
            {conflicts.length === 0 ? (
              <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--accent-green)' }}>
                <span>✓</span><span>No open conflicts</span>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {conflicts.slice(0, 10).map((c) => (
                  <div
                    key={c.id}
                    className="rounded-xl p-3"
                    style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)' }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-text-primary">{c.kind}</span>
                      <span
                        className="text-[10px] rounded px-1.5 py-0.5 font-semibold"
                        style={{
                          background: c.severity === 'critical' ? 'rgba(238,93,80,0.15)' : 'rgba(255,209,102,0.12)',
                          color: c.severity === 'critical' ? 'var(--accent-red)' : 'var(--accent-yellow)',
                        }}
                      >
                        {c.severity}
                      </span>
                    </div>
                    <div className="text-[10px] text-text-dimmed mt-0.5">{timeAgo(c.createdAt)}</div>
                  </div>
                ))}
                {conflicts.length > 10 && (
                  <div className="text-xs text-text-dimmed px-1">+{conflicts.length - 10} more</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Jira tickets */}
        <div className="col-span-7">
          <div className="glass rounded-2xl p-5" style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-text-secondary">
                Jira Tickets
                {jiraIssues.length > 0 && (
                  <span className="ml-2 text-text-dimmed">
                    {openTickets.length} open · {doneTickets.length} done
                  </span>
                )}
              </div>
            </div>
            {jiraIssues.length === 0 ? (
              <div className="text-sm text-text-dimmed">No Jira issues found.</div>
            ) : (
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {jiraIssues.slice(0, 12).map((t) => (
                  <a
                    key={t.key}
                    href={t.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 rounded-xl px-3 py-2 transition-colors"
                    style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)' }}
                  >
                    <span className="text-[10px] font-mono text-text-dimmed shrink-0 w-20">{t.key}</span>
                    <span className="text-xs text-text-secondary truncate flex-1">{t.summary}</span>
                    <span
                      className="text-[10px] shrink-0 rounded px-1.5 py-0.5"
                      style={{
                        background: t.statusCategory === 'done'
                          ? 'rgba(13,207,184,0.12)'
                          : t.statusCategory === 'indeterminate'
                          ? 'rgba(97,218,251,0.12)'
                          : 'var(--bg-secondary)',
                        color: t.statusCategory === 'done'
                          ? 'var(--accent-green)'
                          : 'var(--text-dimmed)',
                      }}
                    >
                      {t.status}
                    </span>
                    {t.goLiveDate && (
                      <span className="text-[10px] shrink-0 text-text-dimmed">{t.goLiveDate}</span>
                    )}
                  </a>
                ))}
                {jiraIssues.length > 12 && (
                  <div className="text-xs text-text-dimmed px-1">+{jiraIssues.length - 12} more</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── KB tab (extracted from old EhrDetailPage) ──────────────────────────────────

function HistoryModal({ articleId, onClose }: { articleId: string; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['kb-revisions', articleId],
    queryFn: () => api.getKbRevisions(articleId),
  });
  const revisions: KbRevision[] = data?.revisions ?? [];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.65)' }} onClick={onClose}>
      <div className="relative w-full max-w-2xl rounded-2xl overflow-hidden" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', maxHeight: '80vh' }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <span className="text-sm font-semibold text-text-primary">Edit History</span>
          <button type="button" onClick={onClose} className="text-text-dimmed hover:text-text-secondary text-lg leading-none">×</button>
        </div>
        <div className="overflow-y-auto p-4 space-y-3" style={{ maxHeight: 'calc(80vh - 60px)' }}>
          {isLoading && <div className="text-sm text-text-dimmed p-4">Loading…</div>}
          {!isLoading && revisions.length === 0 && <div className="text-sm text-text-dimmed p-4">No edit history yet.</div>}
          {revisions.map((rev) => (
            <div key={rev.id} className="rounded-xl p-4" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-text-primary">{rev.editorName ?? 'Unknown'}</span>
                <span className="text-xs text-text-dimmed">{new Date(rev.createdAt).toLocaleString()}</span>
              </div>
              {rev.titleBefore !== rev.titleAfter && rev.titleBefore !== null && (
                <div className="text-xs mb-1">
                  <span className="text-text-dimmed">Title: </span>
                  <span style={{ color: 'var(--accent-red)', textDecoration: 'line-through' }}>{rev.titleBefore}</span>{' → '}
                  <span style={{ color: 'var(--accent-green)' }}>{rev.titleAfter}</span>
                </div>
              )}
              <div className="text-xs text-text-dimmed">Body changed ({rev.bodyBefore.length} → {rev.bodyAfter.length} chars)</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CommentThread({ articleId }: { articleId: string }) {
  const qc = useQueryClient();
  const [commentText, setCommentText] = useState('');
  const { data } = useQuery({ queryKey: ['kb-article', articleId], queryFn: () => api.getKbArticle(articleId) });
  const addComment = useMutation({
    mutationFn: (body: string) => api.addKbComment(articleId, { body }),
    onSuccess: () => { setCommentText(''); void qc.invalidateQueries({ queryKey: ['kb-article', articleId] }); },
    onError: (err) => toast.error(`Comment failed: ${err instanceof Error ? err.message : 'Unknown error'}`),
  });
  const comments = data?.comments ?? [];
  return (
    <div className="px-8 pb-8 space-y-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-text-secondary">Comments</div>
      {comments.length === 0 && <div className="text-sm text-text-dimmed">No comments yet.</div>}
      {comments.map((c) => (
        <div key={c.id} className="rounded-xl p-3" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium text-text-primary">{c.authorName ?? 'Anonymous'}</span>
            <span className="text-xs text-text-dimmed">{timeAgo(c.createdAt)}</span>
          </div>
          <div className="text-sm text-text-secondary whitespace-pre-wrap">{c.body}</div>
        </div>
      ))}
      <div className="flex gap-2 mt-2">
        <input
          type="text"
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && commentText.trim()) { e.preventDefault(); addComment.mutate(commentText.trim()); } }}
          placeholder="Add a comment… (Enter to submit)"
          className="flex-1 rounded-xl px-3 py-2 text-sm outline-none"
          style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
        />
        <button type="button" disabled={!commentText.trim() || addComment.isPending} onClick={() => addComment.mutate(commentText.trim())} className="rounded-xl px-4 py-2 text-sm font-medium disabled:opacity-40" style={{ background: 'var(--accent-blue)', color: '#fff' }}>
          Post
        </button>
      </div>
    </div>
  );
}

function ArticleEditor({ article, onSaved }: { article: KbArticle; onSaved: () => void }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(article.title);
  const [body, setBody] = useState(article.body ?? '');
  const [showHistory, setShowHistory] = useState(false);
  const lastSavedTitle = useRef(article.title);
  const lastSavedBody = useRef(article.body ?? '');

  useEffect(() => { setTitle(article.title); setBody(article.body ?? ''); lastSavedTitle.current = article.title; lastSavedBody.current = article.body ?? ''; }, [article.id]);

  const saveMut = useMutation({
    mutationFn: (patch: { title?: string; body?: string }) => api.updateKbArticle(article.id, patch),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['kb', article.ehrId] }); onSaved(); },
    onError: (err) => toast.error(`Save failed: ${err instanceof Error ? err.message : 'Unknown error'}`),
  });

  function saveIfChanged() {
    const titleChanged = title !== lastSavedTitle.current;
    const bodyChanged = body !== lastSavedBody.current;
    if (!titleChanged && !bodyChanged) return;
    const patch: { title?: string; body?: string } = {};
    if (titleChanged) patch.title = title;
    if (bodyChanged) patch.body = body;
    lastSavedTitle.current = title;
    lastSavedBody.current = body;
    saveMut.mutate(patch);
  }

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) { if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); saveIfChanged(); } }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  const lastEditedLine = article.lastEditedByName
    ? `Last edited by ${article.lastEditedByName} · ${timeAgo(article.lastEditedAt)}`
    : article.updatedAt ? `Last updated ${timeAgo(article.updatedAt)}` : null;

  return (
    <div className="flex flex-col h-full">
      {showHistory && <HistoryModal articleId={article.id} onClose={() => setShowHistory(false)} />}
      <div className="px-8 pt-8 pb-2">
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} onBlur={saveIfChanged}
          className="w-full bg-transparent text-2xl font-bold text-text-primary outline-none border-b border-transparent focus:border-border transition-colors" placeholder="Article title" />
      </div>
      <div className="px-8 pb-4 flex items-center gap-3">
        {lastEditedLine && <span className="text-xs text-text-dimmed">{lastEditedLine}</span>}
        <button type="button" onClick={() => setShowHistory(true)} className="text-xs hover:text-text-secondary transition-colors" style={{ color: 'var(--accent-blue)' }}>History</button>
        {saveMut.isPending && <span className="text-xs text-text-dimmed">saving…</span>}
      </div>
      <div className="flex-1 px-8 pb-6">
        <textarea value={body} onChange={(e) => { setBody(e.target.value); e.target.style.height = 'auto'; e.target.style.height = `${e.target.scrollHeight}px`; }} onBlur={saveIfChanged}
          placeholder="Start writing… (saves on blur or Ctrl+S)"
          className="w-full min-h-64 resize-none bg-transparent text-sm text-text-secondary outline-none leading-relaxed" style={{ minHeight: '16rem' }} />
      </div>
      <div className="border-t border-border mx-8 mb-4" />
      <CommentThread articleId={article.id} />
    </div>
  );
}

function KbTab({ ehrId }: { ehrId: string }) {
  const qc = useQueryClient();
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  const [addingPage, setAddingPage] = useState(false);
  const [newPageTitle, setNewPageTitle] = useState('');
  const newPageInputRef = useRef<HTMLInputElement>(null);

  const { data: kbData } = useQuery({ queryKey: ['kb', ehrId], queryFn: () => api.listKbArticles(ehrId) });
  const kbArticles: KbArticle[] = kbData?.articles ?? [];

  useEffect(() => { if (!selectedArticleId && kbArticles.length > 0) setSelectedArticleId(kbArticles[0]!.id); }, [kbArticles.length]);
  useEffect(() => { if (addingPage) setTimeout(() => newPageInputRef.current?.focus(), 0); }, [addingPage]);

  const createArticle = useMutation({
    mutationFn: (title: string) => api.createKbArticle({ ehrId, title, body: '' }),
    onSuccess: (res) => { void qc.invalidateQueries({ queryKey: ['kb', ehrId] }); setSelectedArticleId(res.article.id); setAddingPage(false); setNewPageTitle(''); toast.success('Page created'); },
    onError: (err) => toast.error(`Failed: ${err instanceof Error ? err.message : 'Unknown error'}`),
  });

  const selectedArticle = kbArticles.find((a) => a.id === selectedArticleId) ?? null;

  function handleAddPageKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && newPageTitle.trim()) createArticle.mutate(newPageTitle.trim());
    else if (e.key === 'Escape') { setAddingPage(false); setNewPageTitle(''); }
  }

  return (
    <div className="flex flex-1 overflow-hidden">
      <aside className="flex flex-col shrink-0 border-r border-border overflow-y-auto" style={{ width: '240px', background: 'var(--glass-bg)' }}>
        <div className="px-4 pt-4 pb-2">
          <div className="text-xs font-semibold uppercase tracking-wider text-text-secondary mb-2">Pages</div>
          {kbArticles.map((a) => (
            <button key={a.id} type="button" onClick={() => setSelectedArticleId(a.id)}
              className="w-full text-left rounded-lg px-3 py-2 text-sm transition-colors mb-0.5"
              style={{ background: selectedArticleId === a.id ? 'var(--bg-tertiary)' : 'transparent', color: selectedArticleId === a.id ? 'var(--text-primary)' : 'var(--text-secondary)', border: selectedArticleId === a.id ? '1px solid var(--border)' : '1px solid transparent', fontWeight: selectedArticleId === a.id ? 600 : 400 }}>
              {a.title}
            </button>
          ))}
          {addingPage ? (
            <div className="mt-1">
              <input ref={newPageInputRef} type="text" value={newPageTitle} onChange={(e) => setNewPageTitle(e.target.value)} onKeyDown={handleAddPageKeyDown} onBlur={() => { if (!newPageTitle.trim()) setAddingPage(false); }}
                placeholder="Page title…" className="w-full rounded-lg px-3 py-2 text-sm outline-none" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--accent-blue)', color: 'var(--text-primary)' }} />
              <div className="text-xs text-text-dimmed mt-1 px-1">Enter to create · Esc to cancel</div>
            </div>
          ) : (
            <button type="button" onClick={() => setAddingPage(true)} className="w-full text-left rounded-lg px-3 py-2 text-sm transition-colors mt-1" style={{ color: 'var(--accent-blue)' }}>
              + Add page
            </button>
          )}
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto" style={{ background: 'var(--bg-primary)' }}>
        {selectedArticle ? (
          <ArticleEditor key={selectedArticle.id} article={selectedArticle} onSaved={() => void qc.invalidateQueries({ queryKey: ['kb', ehrId] })} />
        ) : (
          <div className="flex items-center justify-center h-full text-sm text-text-dimmed">
            {kbArticles.length === 0 ? 'No pages yet. Add one to get started.' : 'Select a page to edit.'}
          </div>
        )}
      </main>
    </div>
  );
}

// ── Jira tab ───────────────────────────────────────────────────────────────────

function JiraTab({ ehrName }: { ehrName: string }) {
  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-2xl">
        <JiraPanel ehrName={ehrName} />
      </div>
    </div>
  );
}

// ── Tab bar ────────────────────────────────────────────────────────────────────

type Tab = 'overview' | 'recipes' | 'kb' | 'jira';

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: 'overview', label: 'Overview',      icon: '◈' },
  { id: 'recipes',  label: 'Recipes',        icon: '⚗' },
  { id: 'kb',       label: 'Knowledge Base', icon: '◎' },
  { id: 'jira',     label: 'Jira',           icon: '⬡' },
];

// ── Main page ──────────────────────────────────────────────────────────────────

export function EhrDetailPage({ ehrId }: { ehrId: string }) {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [showScopeForm, setShowScopeForm] = useState(false);
  const [reviewScopeJobId, setReviewScopeJobId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['ehr', ehrId],
    queryFn: () => api.getEhr(ehrId),
    refetchInterval: 30_000,
  });

  // Single UNCONDITIONAL hook call (must run before the early returns, else the
  // hook count changes render-to-render → React #310).
  usePageTitle(data?.ehr.displayName ?? 'EHR');

  if (isLoading) return <div className="p-8 text-sm text-text-dimmed">Loading…</div>;
  if (!data) return <div className="p-8 text-sm text-text-dimmed">EHR not found.</div>;

  const { ehr, scopeJobs, sessions, conflicts, sessionStats } = data;
  const pendingReview = scopeJobs.find((j) => j.status === 'awaiting_review');

  function handleScopeStarted() {
    setShowScopeForm(false);
    void qc.invalidateQueries({ queryKey: ['ehr', ehrId] });
  }

  return (
    <>
      {reviewScopeJobId && (
        <RecipeReviewModal scopeJobId={reviewScopeJobId} ehrId={ehrId} onClose={() => setReviewScopeJobId(null)} />
      )}

      <div className="flex h-full flex-col">
        {/* ── Header ── */}
        <header className="flex h-14 items-center gap-3 border-b border-border px-6 shrink-0">
          <Link to="/ehrs" className="text-xs text-text-dimmed hover:text-text-secondary">← EHRs</Link>
          <span className="text-text-dimmed">/</span>
          <div className="flex items-center gap-3">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-white"
              style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}>
              {ehr.displayName[0]?.toUpperCase()}
            </div>
            <div>
              <div className="text-sm font-semibold text-text-primary">{ehr.displayName}</div>
              <div className="text-xs text-text-dimmed font-mono">{ehr.slug}</div>
            </div>
          </div>

          {/* Tab bar in header */}
          <div className="flex items-center gap-0.5 ml-6">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                style={activeTab === t.id ? {
                  background: 'linear-gradient(135deg, rgba(13,207,184,0.18) 0%, rgba(6,182,212,0.18) 100%)',
                  color: 'var(--text-primary)',
                  border: '1px solid rgba(13,207,184,0.3)',
                } : {
                  color: 'var(--text-secondary)',
                  border: '1px solid transparent',
                }}
              >
                <span>{t.icon}</span>
                <span>{t.label}</span>
              </button>
            ))}
          </div>

          <div className="ml-auto flex items-center gap-2">
            {pendingReview && (
              <button type="button" onClick={() => setReviewScopeJobId(pendingReview.id)}
                className="rounded-xl px-3 py-1.5 text-xs font-semibold"
                style={{ background: 'var(--accent-yellow)22', color: 'var(--accent-yellow)', border: '1px solid var(--accent-yellow)55' }}>
                Review draft
              </button>
            )}
            {ehr.recipePath && (
              <span className="rounded-full px-2 py-0.5 text-xs font-medium"
                style={{ background: 'var(--accent-green)22', color: 'var(--accent-green)', border: '1px solid var(--accent-green)44' }}>
                ✓ Recipe
              </span>
            )}
            <button type="button" onClick={() => setShowScopeForm(!showScopeForm)}
              className="rounded-xl px-3 py-1.5 text-xs font-semibold text-white"
              style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}>
              + Scope job
            </button>
          </div>
        </header>

        {/* Scope job form (slides in under header, any tab) */}
        {showScopeForm && (
          <div className="glass mx-6 mt-4 rounded-2xl p-6 shrink-0" style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}>
            <div className="mb-4 text-sm font-semibold text-text-primary">New scope job</div>
            <ScopeJobForm ehrId={ehr.id} defaultLoginUrl={ehr.loginUrl} onStarted={handleScopeStarted} onCancel={() => setShowScopeForm(false)} />
          </div>
        )}

        {/* ── Tab content ── */}
        <div className="flex flex-1 overflow-hidden">
          {activeTab === 'overview' && (
            <div className="flex-1 overflow-y-auto">
              <OverviewTab
                ehrId={ehrId}
                ehr={ehr}
                scopeJobs={scopeJobs}
                sessions={sessions}
                conflicts={conflicts ?? []}
                sessionStats={sessionStats ?? { total: 0, completed: 0, running: 0, failed: 0, draft: 0 }}
                onReviewScope={(id) => setReviewScopeJobId(id)}
                onStartScope={() => setShowScopeForm(true)}
              />
            </div>
          )}
          {activeTab === 'recipes' && (
            <div className="flex-1 overflow-y-auto">
              <FlatFileRecipesTab ehrId={ehrId} />
            </div>
          )}
          {activeTab === 'kb' && <KbTab ehrId={ehrId} />}
          {activeTab === 'jira' && <JiraTab ehrName={ehr.displayName} />}
        </div>
      </div>
    </>
  );
}
